// Copyright (c) 2016 Deere & Company

import './maintenance-plan.scss';
import Criteria, {getDefaultState as getCriteriaDefaultState} from '../state/criteria';
import React, {
    Component,
    PropTypes
} from 'react';
import Dealer from '@deere/cps-it.warranty-model-factory/lib/dealer';
import FavoriteIcon from '../../../shared/components/favorites/favorite-icon';
import {FormattedMessage} from 'react-intl';
import Formatting from '../state/formatting';
import GeneralSettings from '@deere/cps-it.warranty-model-factory/lib/general-settings';
import KeyValuePair from '@deere/cps-it.warranty-model-factory/lib/key-value-pair';
import MaintenancePlanCriteria from './maintenance-plan-criteria';
import MaintenancePlanReport from '@deere/cps-it.warranty-model-factory/lib/maintenance-plan-report';
import NumberOfRecordsFound from '../../../shared/components/number-of-records-found/index';
import ReportGrid from '../../shared/components/report-grid';
import ReportHeader from '../../shared/components/report-header';
import ReportHiddenColumns from '../../shared/components/report-columns';
import ReportLayout from '@deere/cps-it.warranty-model-factory/lib/report-layout';
import ShowNoResultsFound from '../../shared/components/show-no-results-found/show-no-results-found';
import View from '../state/view';
import WarrantyAgGridDatePicker from '../../shared/components/ag-grid-date-picker';
import WarrantyLoadingIndicator from '../../../shared/components/warranty-loading-indicator';
import {exportToCSV} from '../../../reports/shared/services/export-to-csv';
import {getAgGridLocaleText} from '../../../shared/utils/ag-grid-locale-text';
import {getDateFormat} from '../../../shared/services/get-date-format';
import {getDefaultReportLayout} from '../../shared/services/report-variant';
import {getMaintenancePlanColumnDefinitions} from '../report-services/column-definitions';
import {scrollToAndResizeReport} from '../../shared/services/scroll-window';

function handleDealersSelectChange(dealer, isSelected) {

    this.actions.updateMaintenancePlanSelectedDealers(dealer, isSelected);

}

function handleSelectAllDealers() {

    this.actions.updateMaintenancePlanSelectAllDealers(this.selectedAccounts);

}

function handlePinInputChange(event) {

    const pin = event.target.value;

    this.actions.updateMaintenancePlanAttributesCriteria('pin', pin);

}

function handleModelInputChange(event) {

    const model = event.target.value;

    this.actions.updateMaintenancePlanAttributesCriteria('model', model);

}

function handlePlanNamesChange(planName) {

    this.actions.updateMaintenancePlanAttributesCriteria('planName', planName);

}

function handleServiceIntervalTypesChange(serviceIntervalType) {

    this.actions.updateMaintenancePlanAttributesCriteria('serviceIntervalType', serviceIntervalType);

}

function handlePlanStartDateChange(planStartDateRange) {

    this.actions.updateMaintenancePlanPlanStartDate(planStartDateRange);

}

function handlePlanEndDateChange(planStartEndDateRange) {

    this.actions.updateMaintenancePlanPlanEndDate(planStartEndDateRange);

}

function handleServiceIntervalBeginDateChange(serviceIntervalBeginDateRange) {

    this.actions.updateMaintenancePlanServiceIntervalBeginDate(serviceIntervalBeginDateRange);

}

function handleServiceIntervalEndDateChange(serviceIntervalTypesEndDateRange) {

    this.actions.updateMaintenancePlanServiceIntervalEndDate(serviceIntervalTypesEndDateRange);

    if (serviceIntervalTypesEndDateRange.from > 0 && serviceIntervalTypesEndDateRange.to > 0) {

        this.actions.updateCriteriaValidity(true);

    } else {

        this.actions.updateCriteriaValidity(false);

    }

}

function handleServiceIntervalStatusesChange(status, isSelected) {

    this.actions.updateMaintenancePlanSelectedServiceIntervalStatuses(status, isSelected);

}

function handleSelectAllServiceIntervalStatuses() {

    this.actions.updateMaintenancePlanSelectAllServiceIntervalStatuses();

}

function handleCreateReportClick() {

    this.actions.toggleReportIsScrollable();
    this.actions.resetSearchResults();
    this.actions.createMaintenancePlanReport();

}

function handleGridApiDefinition(params) {

    let defaultColumnState,
        defaultSortModel;

    this.actions.createAgGridApi(params.api, params.columnApi);

    if (this.selectedLayout && this.selectedLayout.name && !this.isInitialLoad) {

        const defaultLayout = this.layouts.find(layout => !layout.name);

        defaultColumnState = defaultLayout.columnState;
        defaultSortModel = defaultLayout.sortModel;

    } else {

        defaultColumnState = params.columnApi.getColumnState();
        defaultSortModel = params.api.getSortModel();

        this.actions.updateReportProperty('isInitialLoad', false);

    }

    this.actions.updateDefaultLayout(defaultColumnState, defaultSortModel);

    if (this.selectedLayout && this.selectedLayout.name) {

        params.columnApi.setColumnState(this.selectedLayout.columnState);
        params.api.setSortModel(this.selectedLayout.sortModel);
        params.api.setFilterModel(this.selectedLayout.filterModel);

    } else {

        const defaultLayout = getDefaultReportLayout(defaultColumnState, defaultSortModel);

        this.actions.updateReportProperty('selectedLayout', defaultLayout);

    }

}

function onColumnRemoved(columnEvent) {

    if (!columnEvent.visible) {

        this.actions.hideReportColumn(this.columnApi.getColumnState());

    }

}

function handleExportToCsv() {

    const fileName = 'maintenance-plan';

    return exportToCSV(fileName, this.gridApi);

}

function handleResetCriteria() {

    this.actions.resetSearchCriteria(this.selectedAccounts, this.criteria);
    this.actions.resetSearchResults();
    this.actions.updateShowNoResultsFound(false);
    this.actions.updateCriteriaValidity(false);

}

class MaintenancePlan extends Component {

    componentWillMount() {

        this.props.actions.updateServiceIntervalStatuses();
        this.props.actions.updatePlanNames();
        this.props.actions.updateServiceIntervalTypes();
        this.props.actions.updateAllDealersOnLoad(this.props.selectedAccounts);
        this.props.actions.getReportLayouts('maintenance-plan');

    }

    componentDidUpdate(prevProps) {

        if (this.props.reportIsScrollable && this.props.report.length) {

            this.props.actions.resetReportIsScrollable();

            scrollToAndResizeReport();

        }

        const isFirstRun = !prevProps.report.length && !prevProps.showNoResultsFound;
        const layoutWasChanged = this.props.selectedLayout !== prevProps.selectedLayout;
        const readyToApplyLayout = this.props.selectedLayout && Object.keys(this.props.columnApi).length && this.props.isDefaultColumnStateAvailable;

        if ((isFirstRun || layoutWasChanged) && readyToApplyLayout) {

            this.props.columnApi.setColumnState(this.props.selectedLayout.columnState);
            this.props.gridApi.setSortModel(this.props.selectedLayout.sortModel);
            this.props.gridApi.setFilterModel(this.props.selectedLayout.filterModel);

        }

    }

    componentWillUnmount() {

        this.props.actions.resetSearchResults();
        this.props.actions.updateShowNoResultsFound(false);
        this.props.actions.resetSearchCriteria([], getCriteriaDefaultState());
        this.props.actions.updateCriteriaValidity(false);
        this.props.actions.resetReportVariant();

    }

    render() {

        const canRunReport = this.props.criteria.selectedDealers.length > 0 && this.props.isCriteriaValid;

        const gridOptions = {
            dateComponentFramework: WarrantyAgGridDatePicker
        };

        return (
            <section className='maintenance-plan'>
                <h2 className='maintenance-plan-title'>
                    <FormattedMessage id='MAINTENANCE_PLAN'/>
                    <FavoriteIcon
                        id='MAINTENANCE_PLAN'
                        route={this.props.pathName}
                    />
                </h2>
                <MaintenancePlanCriteria
                    canExport={this.props.report.length > 0}
                    canRunReport={canRunReport}
                    criteria={this.props.criteria}
                    dateFormat={getDateFormat(this.props.generalSettings.dateFormat.key)}
                    onCreateReport={this.props::handleCreateReportClick}
                    onExportToCsv={this.props::handleExportToCsv}
                    onHandleDealersSelectChange={this.props::handleDealersSelectChange}
                    onHandleModelInputChange={this.props::handleModelInputChange}
                    onHandlePinInputChange={this.props::handlePinInputChange}
                    onHandlePlanEndDateChange={this.props::handlePlanEndDateChange}
                    onHandlePlanNamesSelectChange={this.props::handlePlanNamesChange}
                    onHandlePlanStartDateChange={this.props::handlePlanStartDateChange}
                    onHandleSelectAllDealers={this.props::handleSelectAllDealers}
                    onHandleSelectAllServiceIntervalStatuses={this.props::handleSelectAllServiceIntervalStatuses}
                    onHandleServiceIntervalBeginDateChange={this.props::handleServiceIntervalBeginDateChange}
                    onHandleServiceIntervalEndDateChange={this.props::handleServiceIntervalEndDateChange}
                    onHandleServiceIntervalStatusesSelectChange={this.props::handleServiceIntervalStatusesChange}
                    onHandleServiceIntervalTypesSelectChange={this.props::handleServiceIntervalTypesChange}
                    planNames={this.props.planNames}
                    resetCriteria={this.props::handleResetCriteria}
                    selectedAccounts={this.props.selectedAccounts}
                    serviceIntervalTypes={this.props.serviceIntervalTypes}
                />
                <WarrantyLoadingIndicator isLoading={this.props.busyCounter > 0}/>
                {this.props.report.length ?
                    <div>
                        <NumberOfRecordsFound count={this.props.report.length}/>
                        <section className='report'>
                            {this.props.isReportColumnsVisible && <ReportHiddenColumns/>}
                            <ReportHeader
                                columnApi={this.props.columnApi}
                                gridApi={this.props.gridApi}
                                reportName='maintenance-plan'
                            />
                            <ReportGrid
                                columnDefs={getMaintenancePlanColumnDefinitions()}
                                gridOptions={gridOptions}
                                localeText={getAgGridLocaleText()}
                                onColumnVisible={this.props::onColumnRemoved}
                                onGridReady={this.props::handleGridApiDefinition}
                                rowData={this.props.report}
                            />
                        </section>
                    </div> :
                    <ShowNoResultsFound showNoResultsFound={this.props.showNoResultsFound}/>
                }
            </section>
        );

    }

}

MaintenancePlan.displayName = 'MaintenancePlan';
MaintenancePlan.propTypes = {
    actions: PropTypes.object.isRequired,
    busyCounter: PropTypes.number.isRequired,
    columnApi: PropTypes.object.isRequired,
    criteria: PropTypes.instanceOf(Criteria).isRequired,
    formatting: PropTypes.instanceOf(Formatting).isRequired,
    generalSettings: PropTypes.instanceOf(GeneralSettings).isRequired,
    gridApi: PropTypes.object.isRequired,
    isCriteriaValid: PropTypes.bool.isRequired,
    isDefaultColumnStateAvailable: PropTypes.bool.isRequired,
    isInitialLoad: PropTypes.bool.isRequired,
    isReportColumnsVisible: PropTypes.bool.isRequired,
    layouts: PropTypes.arrayOf(PropTypes.instanceOf(ReportLayout)).isRequired,
    pathName: PropTypes.string.isRequired,
    planNames: PropTypes.arrayOf(PropTypes.instanceOf(KeyValuePair).isRequired),
    report: PropTypes.arrayOf(PropTypes.instanceOf(MaintenancePlanReport)),
    reportIsScrollable: PropTypes.bool.isRequired,
    selectedAccounts: PropTypes.arrayOf(PropTypes.instanceOf(Dealer)).isRequired,
    selectedLayout: PropTypes.instanceOf(ReportLayout),
    serviceIntervalTypes: PropTypes.arrayOf(PropTypes.instanceOf(KeyValuePair).isRequired),
    showNoResultsFound: PropTypes.bool.isRequired,
    views: PropTypes.arrayOf(PropTypes.instanceOf(View)).isRequired
};

export default MaintenancePlan;



// WEBPACK FOOTER //
// ./src/reports/maintenance-plan/components/maintenance-plan.js